import { useAutoAnimate } from "@formkit/auto-animate/react";
import { useQueryClubFeatureFlagsAsPublic } from "@gymflow/api";
import { AlertProvider } from "@gymflow/common";
import { useContext } from "react";
import {
  matchPath,
  Redirect,
  Route,
  Switch,
  useRouteMatch,
} from "react-router-dom";

import { HostedPagesNavbar } from "../../components/organisms";
import { HostedPagesFooter } from "../../components/site/components/HostedPagesFooter";
import {
  useHtmlNodeDarkMode,
  useInjectWebsiteCustomizations,
} from "../../hooks";
import {
  ClubSettingsAsPublicProvider,
  LoginOverlayProvider,
  ModalProvider,
  useClubSettings,
} from "../../providers";
import { ApiResolverContext } from "../../providers/ApiResolverProvider/ApiResolverContext";
import { AuthenticationProvider } from "../../providers/authentication/AuthenticationProvider";
import { SessionOverlayProvider } from "../../providers/authentication/SessionOverlay/SessionOverlayProvider";
import { OnboardingProvider } from "../../providers/OnboardingProvider/OnboardingProvider";
import {
  generateSiteWithAuthRoutes,
  RouteDefinition,
  RouteFeatureSite,
} from "../../routes";
import useGymflowModels from "../../store";

export function SiteWithAuthWrapper() {
  const { clubId } = useContext(ApiResolverContext);
  return (
    <AuthenticationProvider userType="member">
      <ClubSettingsAsPublicProvider clubId={clubId}>
        <ModalProvider>
          <AlertProvider>
            <OnboardingProvider>
              <SiteWithAuth />
            </OnboardingProvider>
          </AlertProvider>
        </ModalProvider>
      </ClubSettingsAsPublicProvider>
    </AuthenticationProvider>
  );
}

function SiteWithAuth() {
  const match = useRouteMatch();

  const getRoutes = (routes: RouteDefinition[]) => {
    const nodes = routes.map((prop) => {
      return (
        <Route path={match.path + prop.path} key={prop.path}>
          {(props) => <prop.component {...props} />}
        </Route>
      );
    });
    return nodes;
  };

  const { api } = useGymflowModels();
  const { clubId, propertiesSettings } = useClubSettings();

  const { data: featureFlags } = useQueryClubFeatureFlagsAsPublic({
    api,
    clubId,
  });

  const routesWithAuth = generateSiteWithAuthRoutes(featureFlags);

  useHtmlNodeDarkMode({ supportDarkMode: true });
  useInjectWebsiteCustomizations();

  const isStripeEnabled =
    propertiesSettings.club.stripeAccountType === "STANDARD" || "CONNECTED";
  const isNotAvailableRoute = matchPath(
    match.url + RouteFeatureSite.NotAvailable,
    window.location.pathname,
  );
  const [parent] = useAutoAnimate();
  if (!isStripeEnabled && !isNotAvailableRoute) {
    return <Redirect to={match.url + RouteFeatureSite.NotAvailable} />;
  }
  return (
    <SessionOverlayProvider>
      <LoginOverlayProvider>
        <div className="bg-gray-0 flex size-full flex-col justify-between overflow-hidden dark:bg-gray-800">
          <HostedPagesNavbar />
          <div ref={parent} className="flex-1 overflow-y-auto">
            <Switch>{getRoutes(routesWithAuth)}</Switch>
          </div>
          <HostedPagesFooter />
        </div>
      </LoginOverlayProvider>
    </SessionOverlayProvider>
  );
}
